import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { NgCard, NgDivider } from '../../../UI';
import { SlideStatusDot } from '../../../UI/NgIcons';
import { FolderManagerItemActions } from '../../../FolderManager/Components/FolderManagerItemActions';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes } from '../../../../interfaces';
import { FolderManagerActionTypes } from '../../../../interfaces/folderManager.types';

const RAIL_WIDTH = 240;

const StyledRailContainer = styled.div`
  width: ${RAIL_WIDTH}px;
  height: calc(100vh - var(--top-nav-height, 60px) - var(--notifications-height, 0px) - 155px);

  .is-stuck & {
    height: calc(100vh - var(--top-nav-height, 60px) - var(--notifications-height, 0px) - 20px);
  }

  display: flex;
  flex-direction: column;
  overflow: hidden;

  > div {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const StyledHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  padding-bottom: ${({ theme }) => theme.spacing.small};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledTitle = styled.h4`
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text};
  font-size: ${({ theme }) => theme.fontSize.default};
  margin: 0;
`;

const StyledPreviewPill = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 5px 13px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: #374151;
  background: ${({ theme }) => theme.palette.cardBackground};
  border: 1.5px solid #d1d5db;
  border-radius: 99px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.main};
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primaryButton};
    color: ${({ theme }) => theme.palette.primaryButton};
    background: ${({ theme }) => theme.palette.linkBackground};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    pointer-events: none;
  }
`;

const StyledScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
`;

const StyledNavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active ? theme.palette.linkBackground : 'transparent')};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.main};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active, theme }) => ($active ? theme.palette.primaryButton : theme.palette.textMedium)};
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;

  &:hover {
    background: ${({ theme }) => theme.palette.iconButtonHoverBackground};
    color: ${({ theme }) => theme.palette.text};
  }
`;

const StyledAddPageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.main};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primaryButton};
  text-align: left;
  margin-bottom: 4px;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => theme.palette.linkBackground};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledStatusDot = styled.span<{ $status?: 'valid' | 'invalid' | 'empty' }>`
  color: ${({ $status, theme }) => ($status === 'valid' ? theme.palette.success : theme.palette.grayLighter)};
  flex-shrink: 0;
`;

const StyledSectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.palette.textMuted};
  padding: 4px 8px;
  margin-bottom: 2px;
`;

const StyledBottomBar = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
  padding-bottom: ${({ theme }) => theme.spacing.default};
`;

const StyledDynamicPageRow = styled.div<{ $active?: boolean; $isDragging?: boolean; $isOverlay?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  border-radius: 6px;
  margin-bottom: 2px;
  opacity: ${({ $isDragging }) => ($isDragging ? 0 : 1)};
  box-shadow: ${({ $isOverlay }) => ($isOverlay ? '0 4px 12px rgba(0,0,0,0.15)' : 'none')};
  background: ${({ $active, theme }) => ($active ? theme.palette.linkBackground : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.palette.iconButtonHoverBackground};

    .page-actions {
      opacity: 1;
    }

    .drag-handle {
      opacity: 1;
    }
  }
`;

const StyledDragHandle = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 30px;
  color: ${({ theme }) => theme.palette.textMuted};
  cursor: grab;
  opacity: 0;
  transition: opacity 0.15s;

  &:active {
    cursor: grabbing;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StyledPageLabel = styled.button<{ $active?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 4px 7px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.main};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active, theme }) => ($active ? theme.palette.primaryButton : theme.palette.textMedium)};
  text-align: left;
  min-width: 0;
`;

const StyledRenameInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ $hasError, theme }) => ($hasError ? theme.palette.error : theme.palette.primaryButton)};
  border-radius: 6px;
  font-family: ${({ theme }) => theme.fontFamily.main};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  color: ${({ theme }) => theme.palette.text};
  background: white;
  margin-bottom: 2px;
`;

const StyledRenameError = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.error};
  padding: 0 4px 4px;
`;

export type MicrositeTabStatus = 'valid' | 'invalid' | 'empty';

export interface MicrositePagesRailTab {
  label: string;
  value: string;
  status?: MicrositeTabStatus;
  isDynamic?: boolean;
}

interface SortablePageTabProps {
  tab: MicrositePagesRailTab;
  isActive: boolean;
  isRenaming: boolean;
  renameValue: string;
  renameError: string | null;
  onRenameValueChange: (val: string) => void;
  onFinishRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  onTabChange: (value: string) => void;
}

const PAGE_ACTIONS = [
  { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.REMOVE, type: FolderManagerItemActionTypes.SINGLE },
];

const SortablePageTab: FC<SortablePageTabProps> = ({
  tab,
  isActive,
  isRenaming,
  renameValue,
  renameError,
  onRenameValueChange,
  onFinishRename,
  onCancelRename,
  onStartRename,
  onDelete,
  onTabChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.value });
  const inputRef = useRef<HTMLInputElement>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Refocus the input after a validation error (blur caused it to lose focus)
  useEffect(() => {
    if (renameError) {
      inputRef.current?.focus();
    }
  }, [renameError]);

  const handleAction = (action: { actionType: FolderManagerActionTypes }) => {
    if (action.actionType === FolderManagerActionTypes.RENAME) onStartRename();
    if (action.actionType === FolderManagerActionTypes.DELETE) onDelete();
  };

  if (isRenaming) {
    return (
      <>
        <StyledRenameInput
          ref={inputRef}
          autoFocus
          $hasError={!!renameError}
          value={renameValue}
          onChange={(event) => onRenameValueChange(event.target.value)}
          onBlur={onFinishRename}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onFinishRename();
            if (event.key === 'Escape') onCancelRename();
          }}
        />
        {renameError && <StyledRenameError>{renameError}</StyledRenameError>}
      </>
    );
  }

  return (
    <StyledDynamicPageRow ref={setNodeRef} style={style} $active={isActive} $isDragging={isDragging}>
      <StyledDragHandle className="drag-handle" {...listeners} {...attributes}>
        <DragIndicatorIcon />
      </StyledDragHandle>

      <StyledPageLabel $active={isActive} onClick={() => onTabChange(tab.value)}>
        <StyledStatusDot $status={tab.status}>
          <SlideStatusDot filled={tab.status === 'valid'} />
        </StyledStatusDot>
        {tab.label}
      </StyledPageLabel>

      <FolderManagerItemActions
        item={{ id: tab.value, name: tab.label, type: 'page' } as unknown as FolderManagerItem}
        actions={PAGE_ACTIONS}
        onAction={handleAction}
      />
    </StyledDynamicPageRow>
  );
};

interface MicrositePagesRailProps {
  tabs: MicrositePagesRailTab[];
  activeTab: string;
  previewLink?: string;
  footer?: ReactNode;
  onTabChange: (value: string) => void;
  onAddPage?: () => void;
  onRenamePage?: (instanceKey: string, newName: string) => void;
  onDeletePage?: (instanceKey: string) => void;
  onReorderPages?: (newOrder: string[]) => void;
}

export const MicrositePagesRail: FC<MicrositePagesRailProps> = ({
  tabs,
  activeTab,
  previewLink,
  footer,
  onTabChange,
  onAddPage,
  onRenamePage,
  onDeletePage,
  onReorderPages,
}) => {
  const settingsTab = tabs.find((t) => t.value === 'default');
  const staticPageTabs = tabs.filter((t) => t.value !== 'default' && !t.isDynamic);
  const dynamicPageTabs = tabs.filter((t) => t.isDynamic);

  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleStartRename = (tabValue: string, currentLabel: string) => {
    setRenamingKey(tabValue);
    setRenameValue(currentLabel);
    setRenameError(null);
  };

  const handleFinishRename = () => {
    if (!renamingKey || !renameValue.trim()) {
      setRenamingKey(null);
      setRenameValue('');
      setRenameError(null);
      return;
    }

    const isDuplicate = tabs.some(
      (t) => t.value !== renamingKey && t.label.toLowerCase() === renameValue.trim().toLowerCase(),
    );

    if (isDuplicate) {
      setRenameError('A page with this name already exists');
      return;
    }

    if (onRenamePage) {
      onRenamePage(renamingKey, renameValue.trim());
    }
    setRenamingKey(null);
    setRenameValue('');
    setRenameError(null);
  };

  const handleCancelRename = () => {
    setRenamingKey(null);
    setRenameValue('');
    setRenameError(null);
  };

  const handleRenameValueChange = (val: string) => {
    setRenameValue(val);
    if (renameError) setRenameError(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderPages) return;

    const oldIndex = dynamicPageTabs.findIndex((t) => t.value === active.id);
    const newIndex = dynamicPageTabs.findIndex((t) => t.value === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(dynamicPageTabs, oldIndex, newIndex);
    onReorderPages(reordered.map((t) => t.value));
  };

  const pageCount = staticPageTabs.length + dynamicPageTabs.length;

  return (
    <StyledRailContainer>
      <NgCard zIndex={0}>
        <StyledHeader>
          <StyledTitle>My Site</StyledTitle>
          <StyledPreviewPill onClick={() => previewLink && window.open(previewLink, '_blank')} disabled={!previewLink}>
            Preview
          </StyledPreviewPill>
        </StyledHeader>

        <NgDivider minHeight={2} spacing="small" />

        <StyledScrollBody>
          {settingsTab && (
            <StyledNavItem $active={activeTab === 'default'} onClick={() => onTabChange('default')}>
              <StyledStatusDot $status={settingsTab.status}>
                <SlideStatusDot filled={settingsTab.status === 'valid'} />
              </StyledStatusDot>
              Settings
            </StyledNavItem>
          )}

          {onAddPage && <StyledAddPageButton onClick={onAddPage}>+ Add Page</StyledAddPageButton>}

          <StyledSectionLabel>Pages ({pageCount})</StyledSectionLabel>

          {staticPageTabs.map((tab) => (
            <StyledNavItem key={tab.value} $active={activeTab === tab.value} onClick={() => onTabChange(tab.value)}>
              <StyledStatusDot $status={tab.status}>
                <SlideStatusDot filled={tab.status === 'valid'} />
              </StyledStatusDot>
              {tab.label}
            </StyledNavItem>
          ))}

          {dynamicPageTabs.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={dynamicPageTabs.map((t) => t.value)} strategy={verticalListSortingStrategy}>
                {dynamicPageTabs.map((tab) => (
                  <SortablePageTab
                    key={tab.value}
                    tab={tab}
                    isActive={activeTab === tab.value}
                    isRenaming={renamingKey === tab.value}
                    renameValue={renameValue}
                    renameError={renamingKey === tab.value ? renameError : null}
                    onRenameValueChange={handleRenameValueChange}
                    onFinishRename={handleFinishRename}
                    onCancelRename={handleCancelRename}
                    onStartRename={() => handleStartRename(tab.value, tab.label)}
                    onDelete={() => onDeletePage?.(tab.value)}
                    onTabChange={onTabChange}
                  />
                ))}
              </SortableContext>
              <DragOverlay>
                {activeDragId &&
                  (() => {
                    const overlayTab = dynamicPageTabs.find((t) => t.value === activeDragId);
                    if (!overlayTab) return null;
                    return (
                      <StyledDynamicPageRow $isOverlay>
                        <StyledDragHandle>
                          <DragIndicatorIcon />
                        </StyledDragHandle>
                        <StyledPageLabel $active={activeTab === overlayTab.value}>
                          <StyledStatusDot $status={overlayTab.status}>
                            <SlideStatusDot filled={overlayTab.status === 'valid'} />
                          </StyledStatusDot>
                          {overlayTab.label}
                        </StyledPageLabel>
                      </StyledDynamicPageRow>
                    );
                  })()}
              </DragOverlay>
            </DndContext>
          )}
        </StyledScrollBody>

        {footer && (
          <>
            <NgDivider minHeight={2} spacing="small" />
            <StyledBottomBar>{footer}</StyledBottomBar>
          </>
        )}
      </NgCard>
    </StyledRailContainer>
  );
};
